import { z } from "zod";
import { CommitShaSchema, IdSchema, PrincipalSchema, TimestampSchema } from "./primitives.ts";

const NameSchema = z.string().regex(/^[a-z][a-z0-9-]{0,62}$/, "lowercase letters, digits and dashes, starting with a letter");
const BindingSchema = z.string().regex(/^[A-Z][A-Z0-9_]*$/, "binding names are UPPER_SNAKE_CASE");

/**
 * Where the value of a secret lives, tagged on `type`. Values never appear in a manifest, a record or an event.
 * New sources are added only through a contract request. Parsing with this schema rejects a `type` it doesn't
 * know, so a deployer built on an older contract fails the deploy instead of skipping the secret.
 *
 * - `cf-secrets-store`: a secret in a Cloudflare Secrets Store on the deploy account. The deployer binds it to the
 *   Worker as a Secrets Store binding (`secrets_store_secret` in the upload metadata), so the value never passes
 *   through Botallion. Inside the Worker it is an object, not a string: read it with `await env.NAME.get()`.
 */
export const SecretSourceSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("cf-secrets-store"),
    /** Id of the Secrets Store on the deploy account. */
    storeId: z.string().min(1),
    /** Name of the secret inside the store, bound on production deploys, for example `ladder-results-signing-key`. */
    secretName: z.string().min(1),
    /** Secret bound on preview deploys instead, for example a test-mode key. Defaults to `secretName`. */
    previewSecretName: z.string().min(1).optional(),
  }),
]);
/** See {@link SecretSourceSchema}. */
export type SecretSource = z.infer<typeof SecretSourceSchema>;

/** A secret one Worker needs: the binding name the Worker sees and where the deployer gets it from. */
export const StackSecretSchema = z.object({
  /** Binding name inside the Worker, for example `STRIPE_KEY`. Unique among that Worker's bindings. */
  name: BindingSchema,
  /** Where the value lives. See {@link SecretSourceSchema}. */
  source: SecretSourceSchema,
});
/** See {@link StackSecretSchema}. */
export type StackSecret = z.infer<typeof StackSecretSchema>;

/** One Worker in a stack. */
export const StackWorkerSchema = z.object({
  /** Logical name, unique in the stack, for example `api`. Preview and production names derive from it. */
  name: NameSchema,
  /** Entry module, repo-relative, for example `demo/api/src/index.ts`. */
  entry: z.string().min(1),
  /**
   * Production routes or custom domains, for example `api.example.dev/*`. Not sent by the deployer today:
   * previews answer on `{branch}--{resource}--{stack}.botallion.dev` and production on `{resource}--{stack}.botallion.dev`.
   * See `previewHostname` in `hostname.ts`.
   */
  routes: z.array(z.string().min(1)),
  /** Cron triggers, for example `*\/15 * * * *`. */
  crons: z.array(z.string().min(1)).optional(),
  /** Workers compatibility date, for example `2026-09-01`. */
  compatibilityDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Workers compatibility flags, for example `nodejs_compat`. */
  compatibilityFlags: z.array(z.string().min(1)).optional(),
  /** Plain-text environment variables. Never secrets. */
  vars: z.record(z.string(), z.string()).optional(),
  /**
   * Secrets this Worker needs, by binding name and source. The deployer binds every one on each preview and
   * production deploy, and fails the deploy if a source can't be resolved. See {@link StackSecretSchema}.
   */
  secrets: z.array(StackSecretSchema).optional(),
});
/** See {@link StackWorkerSchema}. */
export type StackWorker = z.infer<typeof StackWorkerSchema>;

/** A D1 database, its migrations and which Workers bind it. */
export const StackD1Schema = z.object({
  /** Binding name inside each Worker, for example `DB`. */
  binding: BindingSchema,
  /** Logical database name, for example `ladder`. */
  name: NameSchema,
  /** Repo-relative folder of numbered `.sql` migrations, applied in filename order. */
  migrationsDir: z.string().min(1),
  /** Workers that bind it (logical names). */
  workers: z.array(NameSchema).min(1),
});
/** See {@link StackD1Schema}. */
export type StackD1 = z.infer<typeof StackD1Schema>;

/** An R2 bucket or KV namespace and which Workers bind it. */
export const StackStoreSchema = z.object({
  /** Binding name inside each Worker, for example `AVATARS`. */
  binding: BindingSchema,
  /** Logical resource name, for example `avatars`. */
  name: NameSchema,
  /** Workers that bind it (logical names). */
  workers: z.array(NameSchema).min(1),
});
/** See {@link StackStoreSchema}. */
export type StackStore = z.infer<typeof StackStoreSchema>;

/** A service binding: `worker` calls `service` through `binding`. */
export const StackServiceBindingSchema = z.object({
  /** The calling Worker (logical name). */
  worker: NameSchema,
  /** Binding name inside the caller, for example `API_SERVICE`. Matches contract refs like `binding:API_SERVICE`. */
  binding: BindingSchema,
  /** The Worker being called (logical name). In a preview it resolves to the preview sibling, never production. */
  service: NameSchema,
});
/** See {@link StackServiceBindingSchema}. */
export type StackServiceBinding = z.infer<typeof StackServiceBindingSchema>;

/** Default preview naming pattern. Every preview resource name carries the branch. */
export const DEFAULT_PREVIEW_NAMING = "{stack}-{branch}-{resource}";

/** Default production naming pattern. Production names never carry a branch. */
export const DEFAULT_PRODUCTION_NAMING = "{stack}-{resource}";

/**
 * Describes a whole multi-Worker app so the deployer can stand it up as an isolated preview
 * per branch and promote it to production. Lives in the repo as `botallion.stack.toml` or `.json`.
 */
export const StackManifestSchema = z
  .object({
    /** Stack name, for example `ladder`. */
    name: NameSchema,
    /** Every Worker in the stack. */
    workers: z.array(StackWorkerSchema).min(1),
    /** D1 databases. */
    d1: z.array(StackD1Schema),
    /** R2 buckets. */
    r2: z.array(StackStoreSchema),
    /** KV namespaces. */
    kv: z.array(StackStoreSchema),
    /** Service bindings between Workers in the stack. */
    services: z.array(StackServiceBindingSchema),
    /** Every Worker name exactly once, in deploy order. A service must deploy before the Workers that call it. */
    deployOrder: z.array(NameSchema).min(1),
    /** Pattern for preview resource names. Must contain `{branch}` and `{resource}`; `{stack}` is optional. */
    previewNaming: z.string().min(1),
    /**
     * Pattern for production resource names. Must contain `{resource}` and must not contain `{branch}`;
     * `{stack}` is optional. Defaults to {@link DEFAULT_PRODUCTION_NAMING} when absent.
     */
    productionNaming: z.string().min(1).optional(),
  })
  .superRefine((m, ctx) => {
    const issue = (message: string, path: string) => ctx.addIssue({ code: "custom", message, path: [path] });
    const workers = m.workers.map((w) => w.name);
    const known = new Set(workers);
    if (known.size !== workers.length) issue("worker names must be unique", "workers");
    const order = new Set(m.deployOrder);
    if (order.size !== m.deployOrder.length || order.size !== known.size || workers.some((w) => !order.has(w))) {
      issue("deployOrder must list every worker exactly once", "deployOrder");
    }
    for (const [field, list] of [["d1", m.d1], ["r2", m.r2], ["kv", m.kv]] as const) {
      for (const r of list) for (const w of r.workers) if (!known.has(w)) issue(`${field} ${r.name} binds unknown worker ${w}`, field);
    }
    const pos = new Map(m.deployOrder.map((w, i) => [w, i]));
    for (const s of m.services) {
      if (!known.has(s.worker) || !known.has(s.service)) issue(`service binding ${s.binding} references an unknown worker`, "services");
      else if ((pos.get(s.service) ?? 0) > (pos.get(s.worker) ?? 0)) issue(`${s.service} must deploy before ${s.worker}, which binds it`, "deployOrder");
    }
    // Binding names, vars and secrets included, must be unique within each Worker.
    for (const { name: w, vars, secrets } of m.workers) {
      const names = [
        ...m.d1.filter((r) => r.workers.includes(w)).map((r) => r.binding),
        ...m.r2.filter((r) => r.workers.includes(w)).map((r) => r.binding),
        ...m.kv.filter((r) => r.workers.includes(w)).map((r) => r.binding),
        ...m.services.filter((s) => s.worker === w).map((s) => s.binding),
        ...Object.keys(vars ?? {}),
        ...(secrets ?? []).map((s) => s.name),
      ];
      if (new Set(names).size !== names.length) issue(`worker ${w} has duplicate binding names`, "workers");
    }
    if (!m.previewNaming.includes("{branch}") || !m.previewNaming.includes("{resource}")) {
      issue("previewNaming must contain {branch} and {resource}", "previewNaming");
    }
    if (m.productionNaming !== undefined && (!m.productionNaming.includes("{resource}") || m.productionNaming.includes("{branch}"))) {
      issue("productionNaming must contain {resource} and must not contain {branch}", "productionNaming");
    }
  });
/** See {@link StackManifestSchema}. */
export type StackManifest = z.infer<typeof StackManifestSchema>;

/** Longest name Cloudflare accepts for Workers and most resources we create. */
export const MAX_RESOURCE_NAME_LENGTH = 63;

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function shortHash(s: string): string {
  // FNV-1a 32-bit, base36. Stable across runtimes.
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36).padStart(7, "0").slice(-7);
}

function render(pattern: string, parts: { stack: string; branch: string; resource: string }): string {
  const full = pattern
    .replaceAll("{stack}", slug(parts.stack))
    .replaceAll("{branch}", slug(parts.branch))
    .replaceAll("{resource}", slug(parts.resource))
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
  if (full.length <= MAX_RESOURCE_NAME_LENGTH) return full;
  const hash = shortHash(full);
  return `${full.slice(0, MAX_RESOURCE_NAME_LENGTH - hash.length - 1).replace(/-+$/, "")}-${hash}`;
}

/**
 * The preview name for one resource of one branch, from the manifest's naming pattern.
 * Lowercases and slugs every part. If the result is longer than 63 characters it is cut and
 * given a stable hash suffix, so two long branch names never collide.
 *
 * @example previewName("{stack}-{branch}-{resource}", { stack: "ladder", branch: "feat/Disputes", resource: "api" })
 * // "ladder-feat-disputes-api"
 */
export function previewName(pattern: string, parts: { stack: string; branch: string; resource: string }): string {
  return render(pattern, parts);
}

/**
 * The production name for one resource, from the manifest's production naming pattern.
 * Slugs and truncates exactly like {@link previewName}. Throws if the pattern contains `{branch}`.
 *
 * @example productionName("{stack}-{resource}", { stack: "ladder", resource: "api" })
 * // "ladder-api"
 */
export function productionName(pattern: string, parts: { stack: string; resource: string }): string {
  if (pattern.includes("{branch}")) throw new Error("production naming must not contain {branch}");
  return render(pattern, { ...parts, branch: "" });
}

/**
 * The real Cloudflare name of one resource (Worker, D1, R2 bucket or KV namespace) for a deploy, using the
 * manifest's patterns: `previewNaming` for previews, `productionNaming` (default {@link DEFAULT_PRODUCTION_NAMING})
 * for production. `branch` is ignored for production. The deployer, dashboard and fixtures all name resources
 * through this, so they agree.
 *
 * @example stackResourceName(manifest, { kind: "preview", branch: "wp-22", resource: "api" }) // "ladder-wp-22-api"
 * @example stackResourceName(manifest, { kind: "production", branch: "main", resource: "api" }) // "ladder-api"
 */
export function stackResourceName(
  manifest: Pick<StackManifest, "name" | "previewNaming" | "productionNaming">,
  target: { kind: "preview" | "production"; branch: string; resource: string },
): string {
  if (target.kind === "production") {
    return productionName(manifest.productionNaming ?? DEFAULT_PRODUCTION_NAMING, { stack: manifest.name, resource: target.resource });
  }
  return previewName(manifest.previewNaming, { stack: manifest.name, branch: target.branch, resource: target.resource });
}

/**
 * Lifecycle of a deploy.
 * - `queued`, `deploying`: in flight.
 * - `succeeded`: everything is up. For a preview, this is when promotion can be approved.
 * - `failed`: something broke; see `logsPointer`.
 * - `torn_down`: a preview whose resources have all been removed.
 */
export const DeployStatusSchema = z.enum(["queued", "deploying", "succeeded", "failed", "torn_down"]);
/** See {@link DeployStatusSchema}. */
export type DeployStatus = z.infer<typeof DeployStatusSchema>;

/** One Cloudflare resource a deploy created or updated. */
export const DeployedResourceSchema = z.object({
  /** Resource type. */
  type: z.enum(["worker", "d1", "r2", "kv"]),
  /** The logical name from the manifest, for example `api`. */
  logicalName: NameSchema,
  /** The real name it was deployed as, from {@link stackResourceName}, for example `ladder-feat-disputes-api` or `ladder-api`. */
  name: z.string().min(1),
  /** Cloudflare's id for it (database id, namespace id), when it has one. */
  cloudflareId: z.string().min(1).optional(),
});
/** See {@link DeployedResourceSchema}. */
export type DeployedResource = z.infer<typeof DeployedResourceSchema>;

/**
 * One deploy of a stack for one branch, preview or production: what was created, where it lives
 * and how it went. The deployer upserts it as it progresses; the dashboard shows it.
 */
export const DeployRecordSchema = z
  .object({
    /** Deploy id, assigned by the deployer. Re-send the same id to update the record. */
    id: IdSchema,
    /** Stack name from the manifest. */
    stack: NameSchema,
    /** Branch deployed, for example `main` or a session fork branch. */
    branch: z.string().min(1),
    /** Preview (isolated, per branch) or production. */
    kind: z.enum(["preview", "production"]),
    /** Commit deployed. Null if not yet resolved. */
    commit: CommitShaSchema.nullable(),
    /** Current state, see {@link DeployStatusSchema}. */
    status: DeployStatusSchema,
    /** Every resource created or updated. */
    resources: z.array(DeployedResourceSchema),
    /** Public URL per Worker. */
    urls: z.array(z.object({ worker: NameSchema, url: z.url() })),
    /** Migrations applied by this deploy, per database, in order. */
    migrationsApplied: z.array(z.object({ database: NameSchema, migrations: z.array(z.string().min(1)) })),
    /** Where to read the logs: a URL or a storage key. Null if none. */
    logsPointer: z.string().min(1).nullable(),
    /** For a preview: the human who approved promoting it to production. Null until approved. */
    approvedBy: PrincipalSchema.nullable(),
    /** When promotion was approved. Null until approved. */
    approvedAt: TimestampSchema.nullable(),
    /** When the deploy started. */
    startedAt: TimestampSchema,
    /** When it reached succeeded, failed or torn_down. Null while in flight. */
    finishedAt: TimestampSchema.nullable(),
  })
  .refine((d) => d.kind === "preview" || d.approvedBy === null, {
    message: "only previews carry a promotion approval",
    path: ["approvedBy"],
  })
  .refine((d) => (d.approvedBy === null) === (d.approvedAt === null), {
    message: "approvedBy and approvedAt are set together",
    path: ["approvedAt"],
  });
/** See {@link DeployRecordSchema}. */
export type DeployRecord = z.infer<typeof DeployRecordSchema>;
